import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Mail, MailOpen, Send, Loader2, RefreshCw, Sparkles,
  Trash2, ChevronLeft, Clock, CheckCircle2, AlertCircle, X, ToggleLeft, ToggleRight
} from 'lucide-react';
import { AdminLayout } from './AdminLayout';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  getEmails, getEmail, sendEmailResponse,
  updateEmailDraft, archiveEmail, regenerateAiDraft,
  getSettings, updateSettings
} from '@/lib/api';

const statusConfig = {
  new: { label: 'New', color: 'text-blue-400', bg: 'bg-blue-500/10', icon: Mail },
  ai_drafted: { label: 'AI Drafted', color: 'text-violet-400', bg: 'bg-violet-500/10', icon: Sparkles },
  pending_review: { label: 'Needs Review', color: 'text-amber-400', bg: 'bg-amber-500/10', icon: AlertCircle },
  responded: { label: 'Responded', color: 'text-emerald-400', bg: 'bg-emerald-500/10', icon: CheckCircle2 },
  archived: { label: 'Archived', color: 'text-white/30', bg: 'bg-white/5', icon: Trash2 },
};

export default function AdminInbox() {
  const [emails, setEmails] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [autoRespond, setAutoRespond] = useState(false);

  const loadEmails = () => {
    setLoading(true);
    getEmails(filter)
      .then(data => { setEmails(data.emails || []); setCounts(data.counts || {}); })
      .catch(() => toast.error('Failed to load emails'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadEmails(); }, [filter]);

  useEffect(() => {
    getSettings().then(s => { if (s) setAutoRespond(!!s.email_auto_respond); }).catch(() => {});
  }, []);

  const openEmail = async (id) => {
    setSelected(id);
    setDetailLoading(true);
    try {
      const data = await getEmail(id);
      setDetail(data);
      setDraft(data.ai_draft || '');
    } catch { toast.error('Failed to load email'); }
    finally { setDetailLoading(false); }
  };

  const handleSend = async () => {
    if (!draft.trim()) { toast.error('Write a response first'); return; }
    setSending(true);
    try {
      await sendEmailResponse(selected, draft);
      toast.success('Response sent!');
      setSelected(null);
      setDetail(null);
      loadEmails();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send'); }
    finally { setSending(false); }
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      const result = await regenerateAiDraft(selected);
      setDraft(result.draft);
      setDetail(prev => ({ ...prev, ai_draft: result.draft, ai_classification: result.classification }));
      toast.success('AI draft regenerated');
    } catch { toast.error('Failed to regenerate'); }
    finally { setRegenerating(false); }
  };

  const handleArchive = async (id) => {
    try {
      await archiveEmail(id);
      toast.success('Email archived');
      if (selected === id) { setSelected(null); setDetail(null); }
      loadEmails();
    } catch { toast.error('Failed to archive'); }
  };

  const toggleAutoRespond = async () => {
    const newVal = !autoRespond;
    setAutoRespond(newVal);
    try {
      await updateSettings({ email_auto_respond: newVal });
      toast.success(`Auto-respond ${newVal ? 'ON' : 'OFF'}`);
    } catch { setAutoRespond(!newVal); toast.error('Failed to update'); }
  };

  const filterTabs = [
    { key: null, label: 'All', count: counts.total },
    { key: 'pending_review', label: 'Needs Review', count: counts.pending_review },
    { key: 'responded', label: 'Responded', count: counts.responded },
  ];

  return (
    <AdminLayout title="Email Inbox">
      <div className="space-y-4">
        {/* Header bar */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex gap-2">
            {filterTabs.map(t => (
              <button
                key={t.key || 'all'}
                onClick={() => { setFilter(t.key); setSelected(null); setDetail(null); }}
                className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                  filter === t.key
                    ? 'bg-rtb-purple text-white'
                    : 'text-white/40 hover:text-white/70 hover:bg-white/[0.04]'
                }`}
                data-testid={`filter-${t.key || 'all'}`}
              >
                {t.label} {t.count != null && <span className="ml-1.5 text-xs opacity-60">({t.count})</span>}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-4">
            {/* Auto-respond toggle */}
            <button
              onClick={toggleAutoRespond}
              className="flex items-center gap-2 text-sm"
              data-testid="auto-respond-toggle"
            >
              {autoRespond ? (
                <ToggleRight className="w-6 h-6 text-emerald-400" />
              ) : (
                <ToggleLeft className="w-6 h-6 text-white/30" />
              )}
              <span className={autoRespond ? 'text-emerald-400' : 'text-white/40'}>Auto-respond</span>
            </button>
            <Button
              variant="outline" size="sm"
              onClick={loadEmails} disabled={loading}
              className="border-white/10 text-white/60 hover:text-white rounded-lg"
              data-testid="refresh-inbox-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </div>

        {/* Inbound URL notice */}
        {(() => {
          const url = `${process.env.REACT_APP_BACKEND_URL}/api/webhooks/brevo/inbound`;
          return (
            <div className="p-3 bg-white/[0.02] border border-white/[0.06] rounded-lg">
              <p className="text-white/30 text-xs">
                <span className="text-white/50 font-bold">Inbound Parsing URL:</span>{' '}
                <code className="text-white/50 bg-white/5 px-1.5 py-0.5 rounded text-xs">{url}</code>
                <button
                  onClick={() => {
                    const el = document.createElement('textarea');
                    el.value = url;
                    el.style.position = 'fixed';
                    el.style.opacity = '0';
                    document.body.appendChild(el);
                    el.select();
                    document.execCommand('copy');
                    document.body.removeChild(el);
                    toast.success('Copied!');
                  }}
                  className="ml-2 text-rtb-purple text-xs underline"
                  data-testid="copy-inbound-url"
                >Copy</button>
              </p>
            </div>
          );
        })()}

        <div className="grid lg:grid-cols-5 gap-4" style={{ minHeight: '500px' }}>
          {/* Email List */}
          <div className="lg:col-span-2 space-y-1 overflow-y-auto" style={{ maxHeight: '600px' }}>
            {loading && emails.length === 0 ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="w-6 h-6 text-rtb-purple animate-spin" />
              </div>
            ) : emails.length === 0 ? (
              <div className="p-12 bg-white/[0.04] border border-white/[0.06] rounded-xl text-center">
                <Mail className="w-10 h-10 text-white/20 mx-auto mb-3" />
                <p className="text-white/40">No emails yet</p>
                <p className="text-white/20 text-sm mt-1">Emails will appear here when Brevo inbound parsing is configured.</p>
              </div>
            ) : (
              emails.map((em) => {
                const sc = statusConfig[em.status] || statusConfig.new;
                const isActive = selected === em.id;
                return (
                  <motion.div
                    key={em.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    onClick={() => openEmail(em.id)}
                    className={`p-3.5 rounded-lg cursor-pointer transition-all border ${
                      isActive
                        ? 'bg-rtb-purple/10 border-rtb-purple/30'
                        : 'bg-white/[0.02] border-transparent hover:bg-white/[0.04] hover:border-white/[0.06]'
                    }`}
                    data-testid={`email-row-${em.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 ${sc.bg} flex items-center justify-center rounded-lg flex-shrink-0 mt-0.5`}>
                        <sc.icon className={`w-4 h-4 ${sc.color}`} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-white text-sm font-medium truncate">{em.from_name || em.from_email}</p>
                          <span className="text-white/20 text-xs flex-shrink-0">
                            {em.created_at?.substring(5, 16).replace('T', ' ')}
                          </span>
                        </div>
                        <p className="text-white/60 text-sm truncate">{em.subject}</p>
                        <p className="text-white/25 text-xs truncate mt-0.5">
                          {em.body_text?.substring(0, 80) || 'No preview'}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                );
              })
            )}
          </div>

          {/* Email Detail */}
          <div className="lg:col-span-3">
            <AnimatePresence mode="wait">
              {!selected ? (
                <motion.div
                  key="empty"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex flex-col items-center justify-center h-full text-center p-12 bg-white/[0.02] border border-white/[0.04] rounded-xl"
                >
                  <MailOpen className="w-12 h-12 text-white/10 mb-3" />
                  <p className="text-white/30">Select an email to view</p>
                </motion.div>
              ) : detailLoading ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="w-6 h-6 text-rtb-purple animate-spin" />
                </div>
              ) : detail ? (
                <motion.div
                  key={selected}
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="bg-white/[0.04] border border-white/[0.06] rounded-xl overflow-hidden flex flex-col"
                  style={{ maxHeight: '600px' }}
                >
                  {/* Email header */}
                  <div className="p-4 border-b border-white/[0.06]">
                    <div className="flex items-center justify-between mb-2">
                      <button onClick={() => { setSelected(null); setDetail(null); }} className="text-white/40 hover:text-white">
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                      <div className="flex gap-2">
                        {detail.status !== 'responded' && detail.status !== 'archived' && (
                          <Button
                            size="sm" variant="outline"
                            onClick={() => handleArchive(detail.id)}
                            className="border-white/10 text-white/40 hover:text-red-400 rounded-lg"
                            data-testid="archive-email-btn"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                    <h3 className="font-oswald text-lg font-bold text-white">{detail.subject}</h3>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-white/60 text-sm">{detail.from_name}</span>
                      <span className="text-white/30 text-xs">&lt;{detail.from_email}&gt;</span>
                      <span className="text-white/20 text-xs ml-auto">
                        <Clock className="w-3 h-3 inline mr-1" />
                        {detail.created_at?.substring(0, 16).replace('T', ' ')}
                      </span>
                    </div>
                    {detail.ai_classification && (
                      <span className={`inline-block mt-2 px-2 py-0.5 rounded text-xs ${
                        detail.ai_classification === 'auto' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400'
                      }`}>
                        AI: {detail.ai_classification === 'auto' ? 'Can auto-respond' : 'Needs review'}
                      </span>
                    )}
                  </div>

                  {/* Email body */}
                  <div className="p-4 border-b border-white/[0.06] overflow-y-auto" style={{ maxHeight: '180px' }}>
                    <div className="text-white/70 text-sm whitespace-pre-wrap leading-relaxed">
                      {detail.body_text || 'No text content'}
                    </div>
                  </div>

                  {/* AI Draft / Response */}
                  <div className="p-4 flex-1 overflow-y-auto">
                    {detail.status === 'responded' ? (
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                          <span className="text-emerald-400 text-sm font-bold">Response Sent</span>
                          <span className="text-white/20 text-xs ml-auto">
                            {detail.responded_at?.substring(0, 16).replace('T', ' ')}
                          </span>
                        </div>
                        <div className="p-3 bg-emerald-500/5 border border-emerald-500/10 rounded-lg text-white/60 text-sm whitespace-pre-wrap">
                          {detail.response_sent}
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-violet-400" />
                            <span className="text-violet-400 text-sm font-bold">AI Draft</span>
                          </div>
                          <button
                            onClick={handleRegenerate}
                            disabled={regenerating}
                            className="text-white/30 hover:text-white/60 text-xs flex items-center gap-1"
                            data-testid="regenerate-draft-btn"
                          >
                            <RefreshCw className={`w-3 h-3 ${regenerating ? 'animate-spin' : ''}`} />
                            Regenerate
                          </button>
                        </div>
                        <textarea
                          value={draft}
                          onChange={(e) => setDraft(e.target.value)}
                          className="w-full min-h-[120px] bg-white/[0.04] border border-white/[0.06] text-white/80 p-3 rounded-lg focus:border-rtb-purple focus:outline-none text-sm leading-relaxed"
                          placeholder="AI will generate a response draft..."
                          data-testid="email-draft-textarea"
                        />
                        <div className="flex justify-end mt-3">
                          <Button
                            onClick={handleSend}
                            disabled={sending || !draft.trim()}
                            className="bg-rtb-purple text-white hover:bg-rtb-purple/90 font-oswald font-bold uppercase rounded-lg px-6"
                            data-testid="send-response-btn"
                          >
                            {sending ? (
                              <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Sending...</>
                            ) : (
                              <><Send className="w-4 h-4 mr-2" /> Send Response</>
                            )}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
